import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useLocale } from "../../../context/LanguageContext";
import type { EconomyConfig, UpdateEconomyConfigInput } from "../types";
import { EDITABLE_FIELDS, UNIT_LABEL_KEYS, groupFieldsBySection } from "../constants";

interface EconomyEditorProps {
  config: EconomyConfig;
  onSave: (input: UpdateEconomyConfigInput) => void;
  onCancel: () => void;
  saving: boolean;
}

export function EconomyEditor({ config, onSave, onCancel, saving }: EconomyEditorProps) {
  const { t } = useLocale();
  // Initialize form state from config
  const [form, setForm] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const f of EDITABLE_FIELDS) {
      init[f.key] = String(config[f.key] ?? "");
    }
    return init;
  });

  const [showConfirm, setShowConfirm] = useState(false);

  const set = (key: string, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // Compute diff: only send fields that actually changed
  const computeDiff = (): UpdateEconomyConfigInput => {
    const input: Record<string, number> = {};
    for (const f of EDITABLE_FIELDS) {
      const num = parseFloat(form[f.key]);
      if (!isNaN(num) && num !== config[f.key]) {
        input[f.key] = num;
      }
    }
    return input as UpdateEconomyConfigInput;
  };

  const diff = computeDiff();
  const changedFields = Object.keys(diff);
  const hasChanges = changedFields.length > 0;

  // Validate prize pool + burn sum to 1.0
  const poolVal = parseFloat(form.challengePrizePoolPercentage);
  const burnVal = parseFloat(form.challengeHouseBurnPercentage);
  const poolBurnValid = isNaN(poolVal) || isNaN(burnVal) || Math.abs(poolVal + burnVal - 1.0) < 0.001;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasChanges || !poolBurnValid) return;
    setShowConfirm(true);
  };

  const handleConfirm = () => {
    setShowConfirm(false);
    onSave(diff);
  };

  const sections = groupFieldsBySection(EDITABLE_FIELDS);

  return (
    <form onSubmit={handleSubmit} className="max-w-3xl">
      {/* Warning banner */}
      <div className="mb-6 flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
        <div>
          <p className="text-sm font-medium text-destructive">
            {t("adminUI.economy.warningGlobal")}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t("adminUI.economy.onlyChangedSaved", { version: config.version })}
          </p>
        </div>
      </div>

      {/* Sections */}
      {Object.entries(sections).map(([section, fields]) => (
        <div key={section} className="mb-4 rounded-lg border border-border bg-card p-5">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t(section)}
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {fields.map((f) => {
              const current = config[f.key];
              const entered = parseFloat(form[f.key]);
              const changed = !isNaN(entered) && entered !== current;

              return (
                <div key={f.key}>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">
                    {t(f.labelKey)}
                    {f.unit && (
                      <span className="ml-1 text-muted-foreground/60">
                        ({UNIT_LABEL_KEYS[f.unit] ? t(UNIT_LABEL_KEYS[f.unit]) : f.unit})
                      </span>
                    )}
                  </label>
                  <input
                    type="number"
                    step={f.step ?? "any"}
                    min={f.min}
                    max={f.max}
                    value={form[f.key]}
                    onChange={(e) => set(f.key, e.target.value)}
                    className={`h-9 w-full rounded-md border px-3 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary ${
                      changed
                        ? "border-primary bg-primary/5"
                        : "border-border bg-background"
                    }`}
                  />
                  {f.descriptionKey && (
                    <p className="mt-0.5 text-[11px] text-muted-foreground/70">{t(f.descriptionKey)}</p>
                  )}
                  {changed && (
                    <p className="mt-0.5 text-[11px] font-medium text-primary">
                      {current} → {entered}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Prize pool validation error */}
      {!poolBurnValid && (
        <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
          <p className="text-sm text-destructive">
            {t("adminUI.economy.poolBurnError", {
              pool: poolVal,
              burn: burnVal,
              sum: (poolVal + burnVal).toFixed(2),
            })}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={!hasChanges || !poolBurnValid || saving}
          className="inline-flex h-9 items-center rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving
            ? t("adminUI.economy.saving")
            : t("adminUI.economy.saveChanges", { count: changedFields.length })}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="h-9 px-4 text-sm text-muted-foreground hover:text-foreground"
        >
          {t("common.cancel")}
        </button>
      </div>

      {/* Confirmation dialog */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-foreground">
              {t("adminUI.economy.confirmTitle")}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("adminUI.economy.confirmBody")}
            </p>

            {/* Before / After comparison */}
            <div className="mt-4 max-h-60 overflow-y-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 border-b border-border bg-muted/80">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">{t("adminUI.economy.colField")}</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">{t("adminUI.economy.colBefore")}</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">{t("adminUI.economy.colAfter")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {changedFields.map((key) => {
                    const meta = EDITABLE_FIELDS.find((f) => f.key === key);
                    const before = config[key as keyof EconomyConfig];
                    const after = diff[key as keyof UpdateEconomyConfigInput];
                    return (
                      <tr key={key}>
                        <td className="px-3 py-2 text-foreground">{meta ? t(meta.labelKey) : key}</td>
                        <td className="px-3 py-2 text-right text-muted-foreground">{String(before)}</td>
                        <td className="px-3 py-2 text-right font-medium text-primary">{String(after)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-5 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                className="h-9 px-4 text-sm text-muted-foreground hover:text-foreground"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={saving}
                className="inline-flex h-9 items-center rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {saving ? t("adminUI.economy.saving") : t("adminUI.economy.confirmSave")}
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}
